import { UnlinkSDK as CoreUnlinkSDK } from '../core';
import { randomFill } from './src/randomFill.browser';
import { createSenderHmacProof } from './src/createSenderHmacProof.browser';
import { verifySenderHmacProof } from './src/verifySenderHmacProof.browser';

/**
 * Browser-specific subclass of the core UnlinkSDK.
 * 
 * This class overrides core methods that require environment-specific functionality
 * (like secure random number generation) and injects the appropriate browser
 * implementation (`randomFill` using `crypto.getRandomValues`).
 * 
 * The `Parameters<T>[0]` utility is used to ensure argument type consistency
 * with the core SDK methods.
 */
export class UnlinkSDK extends CoreUnlinkSDK {

  /**
   * Override of the core `createHandshakeSendInstructions` method.
   * Injects the browser-specific `randomFill` implementation.
   */
  override createHandshakeSendInstructions(args: Parameters<CoreUnlinkSDK['createHandshakeSendInstructions']>[0]) {
    return super.createHandshakeSendInstructions({ ...args, randomFill, createSenderHmacProof });
  }

  /**
   * Override of the core `createHandshakeConfirmInstructions` method.
   * Injects the browser-specific `randomFill` implementation.
   */
  override createHandshakeConfirmInstructions(args: Parameters<CoreUnlinkSDK['createHandshakeConfirmInstructions']>[0]) {
    return super.createHandshakeConfirmInstructions({ ...args, randomFill, createSenderHmacProof });
  }

  /**
   * Override of the core `sendMessage` method.
   * Injects the browser-specific `randomFill` implementation.
   */
  override createMessageSendInstructions(args: Parameters<CoreUnlinkSDK['createMessageSendInstructions']>[0]) {
    return super.createMessageSendInstructions({ ...args, randomFill, createSenderHmacProof });
  }

  /**
   * Override of the core `sendData` method.
   * Injects the browser-specific `randomFill` implementation.
   */
  override createDataSendInstructions(args: Parameters<CoreUnlinkSDK['createDataSendInstructions']>[0]) {
    return super.createDataSendInstructions({ ...args, randomFill, createSenderHmacProof });
  }

  /**
   * Override of the core `sendData` method.
   * Injects the browser-specific `randomFill` implementation.
   */
  override readMessages(args: Parameters<CoreUnlinkSDK['readMessages']>[0]) {
    return super.readMessages({ ...args, verifySenderHmacProof });
  }

  /**
   * Override of the core `generateConversationId` method.
   * Injects the browser-specific `randomFill` implementation.
   */
  override generateRandomConversationId(args: Parameters<CoreUnlinkSDK['generateRandomConversationId']>[0]) {
    return super.generateRandomConversationId({ ...args, randomFill });
  }
}